import {
  Component,
  OnInit,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  Output,
  EventEmitter,
  inject,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { AppointmentService } from '../../services/appointment.service';
import { Appointment } from '../../models/appointment.model';
import { AppointmentDetailsModalComponent } from '../appointment-details-modal/appointment-details-modal.component';
import { FilterOptions } from '../appointments-filter-modal/appointments-filter-modal.component';
import { CancelAppointmentModalComponent } from '../cancel-appointment-modal/cancel-appointment-modal.component';
import { CancellationReasonModalComponent } from '../cancellation-reason-modal/cancellation-reason-modal.component';
import { CancellationSuccessModalComponent } from '../cancellation-success-modal/cancellation-success-modal.component';
import { CancellationErrorModalComponent } from '../cancellation-error-modal/cancellation-error-modal.component';
import { RescheduleAppointmentModalComponent } from '../reschedule-appointment-modal/reschedule-appointment-modal.component';
import { MarkCompleteModalComponent } from '../mark-complete-modal/mark-complete-modal.component';
import { ProfileService } from '../../services/profile.service';
import { PackageService } from '../../services/package.service';
import { ApiService } from '../../services/api.service';
import { EmployeeService } from '../../services/employee.service';
import { RoleService } from '../../services/role.service';
import { Subscription, of } from 'rxjs';
import { formatDuration, runRescheduleUpdate } from '../../shared/utils/appointment.util';
import { mapEmployeeIdsToNames } from '../../shared/utils/appointment-filter.util';
import { formatDateTimeWithTimezone, formatLocalDateTime } from '../../shared/utils/date.util';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { computeDropdownPosition } from '../../shared/utils/dropdown-position.util';
import { clearCancellationModalsAndAppointment } from '../../shared/utils/cancellation-modal.util';
import { LoggerService } from '../../services/logger.service';
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
/** Input changes that must refetch the list; the first load is ngOnInit's job. */
const RELOAD_ON_INPUT_CHANGE = [
  'activeFilters',
  'searchTerm',
  'currentLocation',
  'selectedLocationLabels',
  'activeNav',
] as const;

@Component({
  selector: 'app-appointments-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatIconModule,
    PaginationBarComponent,
    MatMenuModule,
    MatCheckboxModule,
    AppointmentDetailsModalComponent,
    CancelAppointmentModalComponent,
    CancellationReasonModalComponent,
    CancellationSuccessModalComponent,
    CancellationErrorModalComponent,
    RescheduleAppointmentModalComponent,
    MarkCompleteModalComponent,
  ],
  templateUrl: './appointments-table.component.html',
  styleUrl: './appointments-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppointmentsTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly logger = inject(LoggerService);
  @Input() activeFilters: FilterOptions | null = null;
  @Input() searchTerm = '';
  @Input() currentLocation = '';
  @Input() selectedLocationLabels: string[] = [];
  @Input() activeNav: 'today' | 'upcoming' | 'past' = 'today';
  @Input() showAddButton = true;
  @Input() isAdminUser = false;
  @Output() addNewAppointment = new EventEmitter<void>();
  @Output() createAnotherAppointment = new EventEmitter<void>();
  @Output() appointmentUpdated = new EventEmitter<void>();

  private _tableScrollWrapper?: ElementRef<HTMLElement>;
  @ViewChild('tableScrollWrapper') set tableScrollWrapper(element: ElementRef<HTMLElement>) {
    if (element) {
      this._tableScrollWrapper = element;
      this.initializeDragScrolling();
    }
  }

  private dragScrollConfig!: DragScrollConfig;

  appointments: Appointment[] = [];

  // Material table properties
  dataSource = new MatTableDataSource<Appointment>();
  displayedColumns: string[] = [
    'appointmentId',
    'status',
    'customer',
    'service',
    'dateTime',
    'duration',
    'employee',
    'location',
    'source',
    'actions',
  ];

  loading = false;
  showDropdown = false;
  selectedAppointment: Appointment | null = null;
  dropdownPosition = { top: 0, left: 0 };

  // For modal
  isModalVisible = false;
  selectedAppointmentForModal: Appointment | null = null;

  // For cancel modals
  isCancelModalVisible = false;
  isCancellationReasonModalVisible = false;
  isCancellationSuccessModalVisible = false;
  isCancellationErrorModalVisible = false;
  appointmentToCancel: Appointment | null = null;

  // For reschedule modal
  isRescheduleModalVisible = false;
  appointmentToReschedule: Appointment | null = null;
  isRescheduleLoading = false;

  // For mark complete modal
  isMarkCompleteModalVisible = false;
  appointmentToMarkComplete: Appointment | null = null;

  // Pagination properties
  currentPage = 0; // PrimeNG uses 0-based indexing
  rowsPerPage = 5;
  totalRecords = 0;

  // Rows per page options for dropdown
  rowsPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
  ];

  // Flag to track if initial load has been completed
  private isLoadingAppointments = false;
  private pendingAppointmentsReload = false;

  // Math reference for template
  Math = Math;

  // Helper method to check if search or filters are active
  get hasActiveSearchOrFilters(): boolean {
    return Boolean(
      this.searchTerm?.trim() ||
      this.activeFilters ||
      this.appliedServiceFilter.length > 0 ||
      this.appliedStatusFilter.length > 0 ||
      this.appliedEmployeeFilter.length > 0 ||
      this.appliedLocationFilter.length > 0 ||
      this.selectedLocationLabels.length > 0
    );
  }

  // Get empty state configuration based on whether search/filters are active
  /**
   * Returns the configuration object for the empty state of the appointments table based on search/filters and user role.
   * @example
   * emptyStateConfig()
   * // { image: 'assets/images/package/Illustrations.svg', title: 'No appointments to show', description: 'Start adding appointments to track all order appointments here.', showAddButton: true }
   * @returns {{image: string, title: string, description: string, showAddButton: boolean}} Empty state configuration object.
   **/
  get emptyStateConfig() {
    if (this.hasActiveSearchOrFilters) {
      return {
        image: 'assets/images/package/Illustrations.svg',
        title: 'No Results Found',
        description: 'Please try again or modify your search or filters.',
        showAddButton: false,
      };
    }
    return {
      image: 'assets/images/package/Illustrations.svg',
      title: 'No appointments to show',
      description: this.isAdminUser
        ? 'Start adding appointments to track all order appointments here.'
        : '',
      showAddButton: this.showAddButton && this.isAdminUser,
    };
  }

  // Sorting state
  currentSortField: string | null = 'appointment_code'; // Default to id column
  currentSortOrder: 'asc' | 'desc' | null = 'asc'; // Default to ascending

  // Service filter properties
  isServiceFilterOpen = false;
  serviceOptions: { label: string; value: string }[] = [];
  selectedServices: string[] = [];
  appliedServiceFilter: string[] = [];
  originalAppointments: Appointment[] = []; // Store original data for filtering

  // Status filter properties
  isStatusFilterOpen = false;
  statusOptions: { label: string; value: string }[] = [
    { label: 'Completed', value: 'completed' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Pending', value: 'pending' },
    { label: 'Canceled', value: 'canceled' },
  ];
  selectedStatuses: string[] = [];
  appliedStatusFilter: string[] = [];

  // Employee filter properties
  isEmployeeFilterOpen = false;
  employeeOptions: { label: string; value: string }[] = [];
  selectedEmployees: string[] = [];
  appliedEmployeeFilter: string[] = [];

  // Location filter properties
  isLocationFilterOpen = false;
  locationOptions: { label: string; value: string }[] = [];
  selectedLocations: string[] = [];
  appliedLocationFilter: string[] = [];

  // Subscription management
  private packageSubscription: Subscription | null = null;
  private employeeSubscription: Subscription | null = null;
  private locationSubscription: Subscription | null = null;
  private routeSubscription: Subscription = new Subscription();

  constructor(
    private appointmentService: AppointmentService,
    private profileService: ProfileService,
    private packageService: PackageService,
    private apiService: ApiService,
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: NotificationService,
    private roleService: RoleService
  ) {}

  // Check if current user is staff (hide reschedule/cancel options for staff)
  get isStaffUser(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  /**
   * Initialize component after construction: waits for location to be set and subscribes to route query parameters to load a specific appointment if an appointmentId is present.
   * @example
   * gOnInit()
   * undefined
   * @param {{void}} {{none}} - No parameters.
   * @returns {{void}} Does not return a value; performs initialization and may subscribe to route query parameters.
   **/
  ngOnInit(): void {
    // Load straight away, unscoped: an empty location selection means every location. The
    // table used to wait for a location to arrive via ngOnChanges, which only ever worked
    // because the page seeded the first branch — with no default there is nothing to wait for.
    this.loadAppointments();

    // Check for appointmentId in URL parameters
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      if (params['appointmentId']) {
        this.logger.debug('ðŸ” Appointment ID found in URL:', params['appointmentId']);
        this.loadAppointmentById(params['appointmentId']);
      }
    });
  }

  /**
   * Handles Angular ngOnChanges lifecycle for the appointments table and reloads appointments when relevant inputs change.
   * @example
   * gOnChanges({ activeFilters: { previousValue: null, currentValue: ['service'] } })
   * void
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object containing previous and current values for inputs; used to detect initial location set or subsequent changes to filters, searchTerm, currentLocation, selectedLocationLabels, or activeNav.
   * @returns {{void}} Void; this method performs side effects by resetting paging/filters and calling loadAppointments.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Reload appointments when filters, search, location, or nav changes
    this.logger.debug('ðŸ”„ AppointmentsTable ngOnChanges triggered:', changes);

    if (changes['activeFilters']) {
      this.logger.debug('ðŸ“ Active filters changed:', {
        previousValue: changes['activeFilters'].previousValue,
        currentValue: changes['activeFilters'].currentValue,
      });
    }

    if (changes['activeNav']) {
      this.logger.debug('ðŸ“… Active navigation changed:', {
        previousValue: changes['activeNav'].previousValue,
        currentValue: changes['activeNav'].currentValue,
      });
    }

    // Subsequent changes only — ngOnInit owns the first load, so skip every firstChange or the
    // table would fetch twice on open.
    const hasRelevantChanges = RELOAD_ON_INPUT_CHANGE.some(
      input => changes[input] && !changes[input].firstChange
    );

    if (hasRelevantChanges) {
      this.logger.debug('Reloading appointments due to filter/search/location/nav changes');

      this.currentPage = 0; // Reset to first page when filters change
      this.appliedServiceFilter = []; // Clear service filter when other filters change
      this.appliedStatusFilter = []; // Clear status filter when other filters change
      this.appliedEmployeeFilter = []; // Clear employee filter when other filters change
      this.appliedLocationFilter = []; // Clear location filter when other filters change
      this.loadAppointments();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showDropdown) {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-dropdown-menu') && !target.closest('.menu-btn')) {
        this.closeDropdown();
      }
    }
  }

  private initializeDragScrolling(): void {
    if (!this._tableScrollWrapper?.nativeElement) return;

    // Cleanup and re-initialize using TableUtils
    const config = TableUtils.reinitializeDragScrolling(this._tableScrollWrapper);
    if (config) {
      this.dragScrollConfig = config;
    }
  }

  /**
   * Loads appointments using current pagination, search term, date and location filters; prevents duplicate concurrent API calls by queuing a follow-up reload if a load is already in progress.
   * @example
   * loadAppointments()
   * undefined
   * @returns {void} Does not return a value; updates component state and triggers appointment API call(s).
   */
  loadAppointments(): void {
    // Prevent duplicate concurrent API calls
    if (this.isLoadingAppointments) {
      this.pendingAppointmentsReload = true;
      this.logger.debug('â¸ï¸ Appointment load in progress, queueing a follow-up reload');
      return;
    }

    this.isLoadingAppointments = true;
    this.loading = true;

    // Build API parameters
    const params: any = {
      page: this.currentPage + 1, // API is 1-based
      limit: this.rowsPerPage,
    };

    // Add search term if provided
    if (this.searchTerm && this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    // Add date filter based on activeNav
    this.addDateFilter(params);

    // Add location filter (labels when the toolbar has a selection, else the selected id)
    this.addLocationFilter(params);

    // Add filters if active
    if (this.activeFilters) {
      // Map filter arrays to API parameters (this will handle the API call)
      this.mapFiltersToApiParams(params);
    } else {
      // No modal filters, call API directly
      this.callAppointmentAPI(params);
    }
  }

  /**
   * Adds the active tab to the given params as a `filter_by` day bucket ('today', 'upcoming', 'past').
   * @example
   * addDateFilter({})
   * undefined
   * @param {{any}} {{params}} - Params object to be mutated with the `filter_by` day bucket.
   * @returns {{void}} No return value; mutates the params object in-place.
   **/
  private addDateFilter(params: any): void {
    // CWS-589: the tab is a whole-day bucket, cut at midnight in the *location's* timezone by
    // the API (same boundary the stats cards use). Sending browser-local instants put an
    // appointment at 2pm shop time on a different calendar day for staff in another timezone,
    // so the Today tab listed past appointments and dropped today's. The toolbar location
    // selector is multi-select too, and only the API can bucket per location timezone.
    if (this.activeNav) {
      params.filter_by = this.activeNav;
      this.logger.debug('📅 Date filter applied:', { filter_by: this.activeNav });
      return;
    }

    this.logger.debug('📅 No date filter applied');
  }

  /**
   * Apply a branch filter to the provided params based on the current selected location.
   * @example
   * addLocationFilter({})
   * undefined
   * @param {{any}} {{params}} - Params object that will receive a branch property when the selected location has a branch.
   * @returns {{void}} Does not return a value.
   **/
  private addLocationFilter(params: any): void {
    try {
      // Get current locations from the profile service
      const currentLocations = (this.profileService.locations$ as any).source?.value || [];

      // CWS-587: the toolbar location selector is multi-select, and the calendar sends every
      // selected branch. Match it here so both views scope to the same locations; the
      // single-id lookup below stays as the fallback.
      if (this.selectedLocationLabels && this.selectedLocationLabels.length > 0) {
        params.branch = this.selectedLocationLabels.join(',');
        this.logger.debug('📍 Location filter applied:', { branches: params.branch });
        return;
      }

      // Nothing picked means every location: send no branch at all. The API applies the filter
      // only when it is present, and the list query is already tenant-scoped, so omitting it
      // returns this shop's locations and no one else's.
      if (!this.currentLocation) {
        this.logger.debug('No location selected; showing all locations');
        return;
      }

      // Find the selected location by ID and get its branch name
      const selectedLocation = currentLocations.find((loc: any) => loc.id === this.currentLocation);

      if (selectedLocation && selectedLocation.branch) {
        params.branch = selectedLocation.branch;
        this.logger.debug('ðŸ“ Location filter applied:', {
          locationId: this.currentLocation,
          branchName: selectedLocation.branch,
        });
      } else {
        this.logger.warn('âš ï¸ Could not find location branch name for ID:', this.currentLocation);
        // If locations are not loaded yet, try to load them and retry
        if (currentLocations.length === 0) {
          this.logger.debug('ðŸ”„ Locations not loaded yet, loading locations...');
          this.profileService.loadLocations();
        }
      }
    } catch (error) {
      this.logger.error('âŒ Error applying location filter:', error);
    }
  }

  /**
   * Map the component's activeFilters into API-ready query parameters and invoke the appointments API.
   * @example
   * mapFiltersToApiParams({})
   * undefined
   * @param {{any}} {{params}} - The params object to populate with mapped API query parameters.
   * @returns {{void}} Returns nothing; triggers an API call (may be asynchronous if employee IDs require name mapping).
   **/
  private mapFiltersToApiParams(params: any): void {
    if (!this.activeFilters) return;

    this.logger.debug('ðŸŽ¯ Mapping filters to API params:', this.activeFilters);

    // Get current data from observables to map IDs to names
    let currentLocations: any[] = [];
    let currentPackages: any[] = [];

    try {
      currentLocations = (this.profileService.locations$ as any).source?.value || [];
      // For admin users, get packages from service observable
      if (this.isAdminUser) {
        currentPackages = (this.packageService.packages$ as any).source?.value || [];
      }
    } catch (error) {
      this.logger.warn('âš ï¸ Could not access service data for filter mapping:', error);
    }

    this.logger.debug('ðŸ“ Available locations for mapping:', currentLocations);
    this.logger.debug('ðŸ“¦ Available packages for mapping:', currentPackages);

    // Map appointment statuses
    if (this.activeFilters.statuses.length > 0) {
      params.appointment_status = this.activeFilters.statuses.join(',');
    }

    // Map employee IDs to names
    if (this.activeFilters.employees.length > 0) {
      this.logger.debug('ðŸ‘¥ Selected employee IDs:', this.activeFilters.employees);
      this.mapEmployeeIdsToNames(this.activeFilters.employees)
        .then(employeeNames => {
          // if (employeeNames.length > 0) {
          params.employee_name = employeeNames.join(',');
          this.logger.debug('ðŸ‘¥ Mapped employee names:', employeeNames);

          // Re-call the API with the updated params including employee names
          this.callAppointmentAPI(params);
          // }
        })
        .catch(error => {
          this.logger.error('âŒ Error mapping employee IDs to names:', error);
          // Continue with the API call without employee filter
          this.callAppointmentAPI(params);
        });

      // Return early to wait for employee name mapping
      return;
    }

    // Map location IDs to branch names (from modal filters)
    if (this.activeFilters.locations.length > 0) {
      const selectedLocationNames = currentLocations
        .filter((loc: any) => this.activeFilters!.locations.includes(loc.id))
        .map((loc: any) => loc.branch);
      if (selectedLocationNames.length > 0) {
        // Modal location filters override dropdown location filter
        params.branch = selectedLocationNames.join(',');
        this.logger.debug(
          'ðŸ“ Modal location filters override dropdown filter:',
          selectedLocationNames
        );
      }
    }

    // Map package IDs to package names
    if (this.activeFilters.services.length > 0) {
      // For staff users, fetch packages by location first
      if (!this.isAdminUser) {
        this.mapPackageIdsToNamesForStaff(params, currentLocations);
        return; // Return early, will call API after fetching packages
      } else {
        // For admin users, use packages from service
        const selectedPackageNames = currentPackages
          .filter((pkg: any) => this.activeFilters!.services.includes(pkg.id))
          .map((pkg: any) => pkg.package_name);
        this.logger.debug('ðŸŽª Selected package IDs:', this.activeFilters.services);
        this.logger.debug('ðŸŽª Mapped package names:', selectedPackageNames);
        if (selectedPackageNames.length > 0) {
          params.package_name = selectedPackageNames.join(',');
        }
      }
    }

    this.logger.debug('ðŸš€ Final API parameters after mapping:', params);

    // If no employees to map, call API directly
    this.callAppointmentAPI(params);
  }

  /**
   * Map selected package IDs to package names for the current employee location, attach a comma-separated package_name to params, and call the appointment API.
   * @example
   * mapPackageIdsToNamesForStaff({ services: ['pkg1','pkg2'] }, [{ id: 1 }])
   * undefined
   * @param {{any}} {{params}} - Params object to be augmented with a comma-separated package_name string before calling the appointment API.
   * @param {{any[]}} {{currentLocations}} - Array of current employee location objects (provided for context; not directly used in mapping).
   * @returns {{void}} No return value; performs side effects by calling the appointment API.
   **/
  private mapPackageIdsToNamesForStaff(params: any, currentLocations: any[]): void {
    this.packageService.getPackagesForCurrentEmployeeLocation().subscribe(packages => {
      const selectedPackageNames = packages
        .filter((pkg: any) => this.activeFilters!.services.includes(pkg.id))
        .map((pkg: any) => pkg.package_name);
      this.logger.debug('ðŸŽª Selected package IDs:', this.activeFilters!.services);
      this.logger.debug('ðŸŽª Mapped package names:', selectedPackageNames);
      if (selectedPackageNames.length > 0) {
        params.package_name = selectedPackageNames.join(',');
      }
      this.logger.debug('ðŸš€ Final API parameters after mapping:', params);
      this.callAppointmentAPI(params);
    });
  }

  private async mapEmployeeIdsToNames(employeeIds: string[]): Promise<string[]> {
    return mapEmployeeIdsToNames(employeeIds, this.profileService, this.apiService);
  }

  /**
   * Load appointments from the server using the provided parameters, update component state, apply filters/sorting, and manage loading/error states.
   * @example
   * callAppointmentAPI({ page: 1, pageSize: 20 })
   * undefined
   * @param {{any}} {{params}} - Parameters for the appointments API call (query/pagination/etc.).
   * @returns {{void}} - No return value; performs side effects on component state (appointments, originalAppointments, totalRecords, loading flags).
   **/
  private callAppointmentAPI(params: any): void {
    this.logger.debug('ðŸ“¡ Loading appointments with params:', params);

    this.appointmentService.getAppointments(params).subscribe({
      next: response => {
        this.logger.debug('âœ… API Response received:', response);
        this.logger.debug('ðŸ“Š Appointments data:', response.data?.appointments);
        this.logger.debug('ðŸ“ˆ Total records:', response.data?.pagination?.total);

        if (response.data && response.data.appointments) {
          this.originalAppointments = response.data.appointments; // Store original data
          this.applyServiceFilter(); // Apply any existing service filter
          this.applyStatusFilter(); // Apply any existing status filter
          this.applyEmployeeFilter(); // Apply any existing employee filter
          this.applyLocationFilter(); // Apply any existing location filter
          this.totalRecords = response.data?.pagination?.total || 0;
          this.logger.debug('ðŸ”„ Table updated with', this.appointments.length, 'appointments');

          // Apply client-side sorting if there's an active sort
          if (this.currentSortField && this.currentSortOrder) {
            this.performClientSideSort();
          }
        } else {
          this.logger.warn('âš ï¸ No appointments data in response');
          this.originalAppointments = [];
          this.appointments = [];
          this.dataSource.data = [];
          this.totalRecords = 0;
        }
        this.loading = false;
        this.isLoadingAppointments = false; // Reset loading flag
        this.flushPendingReload();
      },
      error: error => {
        this.logger.error('âŒ Error fetching appointments:', error);
        this.originalAppointments = [];
        this.appointments = [];
        this.totalRecords = 0;
        this.dataSource.data = [];
        this.loading = false; // Ensure loader stops on error
        this.isLoadingAppointments = false; // Reset loading flag
        this.flushPendingReload();
      },
    });
  }

  private flushPendingReload(): void {
    if (!this.pendingAppointmentsReload) {
      return;
    }

    this.pendingAppointmentsReload = false;
    this.loadAppointments();
  }

  /**
   * Toggle the appointment actions dropdown: stops event propagation and opens or closes the dropdown while setting the selected appointment and dropdown position.
   * @example
   * oggleDropdown(sampleEvent, sampleAppointment)
   * undefined
   * @param {{Event}} {{event}} - The DOM event that triggered the toggle; propagation is stopped to avoid triggering parent handlers.
   * @param {{Appointment}} {{appointment}} - The appointment to select when opening the dropdown or the appointment associated with the action.
   * @returns {{void}} No return value.
   **/
  toggleDropdown(event: Event, appointment: Appointment): void {
    event.stopPropagation();

    if (this.showDropdown && this.selectedAppointment === appointment) {
      this.closeDropdown();
      return;
    }

    const target = event.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      this.dropdownPosition = computeDropdownPosition(target);
      this.selectedAppointment = appointment;
      this.showDropdown = true;
    });
  }

  closeDropdown(): void {
    this.showDropdown = false;
    this.selectedAppointment = null;
  }

  /**
   * Map an appointment status to a UI severity label used for styling.
   * @example
   * etStatusSeverity('completed')
   * 'success'
   * @param {string} status - The appointment status to map to a severity.
   * @returns {'success'|'info'|'warn'|'danger'} The corresponding severity label for the provided status.
   **/
  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'confirmed':
        return 'info';
      case 'pending':
        return 'warn';
      case 'cancelled':
        return 'danger';
      default:
        return 'info';
    }
  }

  /**
   * Convert an appointment status key into a human-readable label.
   * @example
   * etStatusLabel('completed')
   * 'Completed'
   * @param {string} status - The status identifier to convert (e.g., 'completed', 'confirmed', 'pending', 'cancelled').
   * @returns {string} The human-readable label for the provided status, or the original status if it is unrecognized.
   */
  getStatusLabel(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'Completed';
      case 'confirmed':
        return 'Confirmed';
      case 'pending':
        return 'Pending';
      case 'cancelled':
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  }

  /** CSS modifier class for the status pill (normalized casing). */
  getStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase().trim();
    return normalized ? `status-${normalized}` : '';
  }

  formatDateTime(dateString: string): string {
    return formatDateTimeWithTimezone(dateString);
  }

  formatAppointmentDisplayTime(appointment: Appointment): string {
    return appointment.appointment_time_local
      ? formatLocalDateTime(appointment.appointment_time_local)
      : this.formatDateTime(appointment.appointment_time);
  }

  markComplete(appointment: Appointment): void {
    this.logger.debug('Mark complete clicked for:', appointment.id);
    this.appointmentToMarkComplete = appointment;
    this.isMarkCompleteModalVisible = true;
  }

  /**
   * Mark an appointment as completed by calling the update API, updating local state, closing the modal, and emitting an update event.
   * @example
   * nMarkCompleteConfirm(appointment)
   * void
   * @param {Appointment} appointment - Appointment object to mark as completed.
   * @returns {void} No return value; performs side effects (API call, UI/state updates, and emits an appointmentUpdated event).
   **/
  onMarkCompleteConfirm(appointment: Appointment): void {
    this.logger.debug('Mark complete confirmed for:', appointment.id);

    // Create payload with only the status change, keeping all other data the same
    const payload = {
      appointment_status: 'completed',
    };

    // Call the API to update the appointment
    this.appointmentService.updateAppointment(appointment.id, payload).subscribe({
      next: response => {
        this.logger.debug('âœ… Appointment marked as completed successfully:', response);

        // Update the local appointment object to reflect the change
        appointment.appointment_status = 'completed';
        appointment.canMarkComplete = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Appointment complete!',
          detail: 'The appointment has been marked complete successfully.',
        });

        // Close the modal
        this.isMarkCompleteModalVisible = false;
        this.appointmentToMarkComplete = null;
        this.loadAppointments();
        // Emit event to parent component to reload appointment stats
        this.appointmentUpdated.emit();
      },
      error: error => {
        this.logger.error('âŒ Error marking appointment as completed:', error);
        // Close the modal even on error
        this.isMarkCompleteModalVisible = false;
        this.appointmentToMarkComplete = null;
        // You could show an error toast here
      },
    });
  }

  onMarkCompleteModalClose(): void {
    this.isMarkCompleteModalVisible = false;
    this.appointmentToMarkComplete = null;
  }

  addInfoAndConfirm(appointment: Appointment): void {
    this.logger.debug('Add info and confirm:', appointment.id);
    // Open modal to add missing information
  }

  viewAppointment(appointment: Appointment): void {
    this.selectedAppointmentForModal = appointment;
    this.isModalVisible = true;
    this.closeDropdown();
  }

  rescheduleAppointment(appointment: Appointment): void {
    this.logger.debug('Reschedule appointment:', appointment.id);
    this.appointmentToReschedule = appointment;
    this.isRescheduleModalVisible = true;
    this.closeDropdown();
  }

  cancelAppointment(appointment: Appointment): void {
    this.logger.debug('Cancel appointment:', appointment.id);
    this.appointmentToCancel = appointment;
    this.isCancelModalVisible = true;
    this.closeDropdown();
  }

  onCancelConfirm(appointment: Appointment): void {
    this.isCancelModalVisible = false;
    this.isCancellationReasonModalVisible = true;
  }

  /**
   * Cancels the given appointment by sending a cancellation payload to the appointment service and updates modal state based on the result.
   * @example
   * nCancelReasonSubmit({ item: { id: 'abc123' }, reason: 'Client requested cancellation' })
   * void
   * @param {{item: Appointment | any, reason: string}} {{data}} - Object containing the appointment to cancel (item) and the cancellation reason (reason).
   * @returns {{void}} Void.
   **/
  onCancelReasonSubmit(data: { item: Appointment | any; reason: string }): void {
    const payload = {
      appointment_status: 'cancelled',
      cancellation_reason: data.reason,
      cancellation_date: new Date().toISOString(),
    };

    this.appointmentService.cancelAppointment(data.item.id, payload).subscribe({
      next: response => {
        this.logger.debug('âœ… Appointment cancelled successfully:', response);
        this.isCancellationReasonModalVisible = false;
        this.isCancellationSuccessModalVisible = true;
        // Reload appointments to reflect the change
        this.loadAppointments();
      },
      error: error => {
        this.logger.error('âŒ Error cancelling appointment:', error);
        this.isCancellationReasonModalVisible = false;
        this.isCancellationErrorModalVisible = true;
        this.appointmentToCancel = null;
      },
    });
  }

  onCancelModalClose(): void {
    this.isCancelModalVisible = false;
    this.appointmentToCancel = null;
  }

  onCancellationReasonModalClose(): void {
    this.isCancellationReasonModalVisible = false;
    this.appointmentToCancel = null;
  }

  onCancellationSuccessModalOkay(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationSuccessModalCreateAnother(): void {
    clearCancellationModalsAndAppointment(this);
    this.createAnotherAppointment.emit();
  }

  onCancellationSuccessModalClose(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationErrorModalOkay(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationErrorModalClose(): void {
    clearCancellationModalsAndAppointment(this);
  }

  // Reschedule modal methods
  onRescheduleModalClose(): void {
    this.isRescheduleModalVisible = false;
    this.appointmentToReschedule = null;
  }

  onRescheduleUpdate(data: { appointmentId: string; payload: any }): void {
    runRescheduleUpdate(data, this.appointmentService, this.messageService, {
      setLoading: v => {
        this.isRescheduleLoading = v;
      },
      closeRescheduleModal: () => {
        this.isRescheduleModalVisible = false;
        this.appointmentToReschedule = null;
      },
      loadAppointments: () => this.loadAppointments(),
    });
  }

  // Load appointment by ID and open details modal
  /**
   * Load appointment details by ID and open the appointment modal.
   * @example
   * loadAppointmentById('12345')
   * void
   * @param {{string}} {{appointmentId}} - The unique identifier of the appointment to load.
   * @returns {{void}} No return value; triggers a service call, sets selectedAppointmentForModal on success, and makes the modal visible.
   **/
  private loadAppointmentById(appointmentId: string): void {
    this.logger.debug('ðŸ“‹ Loading appointment details for ID:', appointmentId);
    this.appointmentService.getAppointmentById(appointmentId).subscribe({
      next: appointment => {
        this.logger.debug('âœ… Appointment loaded successfully:', appointment);
        const listAppointment = this.appointments.find(apt => apt.id === appointmentId);
        this.selectedAppointmentForModal = {
          ...listAppointment,
          ...appointment.data,
          appointment_time_local:
            appointment.data?.appointment_time_local ?? listAppointment?.appointment_time_local,
        };
        this.isModalVisible = true;
      },
      error: error => {
        this.logger.error('âŒ Error loading appointment:', error);
        // Optionally show an error message to the user
      },
    });
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.selectedAppointmentForModal = null;
    // Remove appointmentId from URL when modal is closed
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { appointmentId: null },
      queryParamsHandling: 'merge',
    });
  }

  onAppointmentUpdated(): void {
    this.logger.debug('ðŸ”„ Appointment updated, refreshing table...');
    // Reload appointments in the table
    this.loadAppointments();
    // Emit event to parent component
    this.appointmentUpdated.emit();
  }

  onMarkCompleteFromDetailsModal(appointment: Appointment): void {
    this.logger.debug('Mark complete appointment from details modal:', appointment.id);
    this.appointmentToMarkComplete = appointment;
    this.isMarkCompleteModalVisible = true;
  }

  onRescheduleFromDetailsModal(appointment: Appointment): void {
    this.logger.debug('Reschedule appointment from details modal:', appointment.id);
    this.appointmentToReschedule = appointment;
    this.isRescheduleModalVisible = true;
  }

  onCancelFromDetailsModal(appointment: Appointment): void {
    this.logger.debug('Cancel appointment from details modal:', appointment.id);
    this.isModalVisible = false;
    this.appointmentToCancel = appointment;
    this.isCancelModalVisible = true;
  }

  // Pagination data for the shared pagination bar (1-based page)
  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage + 1,
      totalPages: Math.max(1, Math.ceil(this.totalRecords / this.rowsPerPage)),
      totalItems: this.totalRecords,
      itemsPerPage: this.rowsPerPage,
    };
  }

  // Pagination methods
  onPageChange(page: number): void {
    this.currentPage = page - 1; // Convert 1-based to 0-based
    this.loadAppointments();
  }

  onRowsPerPageChange(newSize: number): void {
    this.rowsPerPage = newSize;
    this.currentPage = 0; // Reset to first page when changing page size
    this.loadAppointments();
  }

  // Sorting methods
  /**
   * Toggle or set the active sort field and order, then trigger a client-side sort.
   * @example
   * nCustomSort('startDate')
   * undefined
   * @param {{string}} {{field}} - Field name to sort by.
   * @returns {{void}} No return value; updates the component's currentSortField/currentSortOrder and performs client-side sorting.
   **/
  onCustomSort(field: string): void {
    if (this.currentSortField === field) {
      // Toggle sort order if same field
      this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New field, start with ascending
      this.currentSortField = field;
      this.currentSortOrder = 'asc';
    }

    // Perform client-side sorting
    this.performClientSideSort();
  }

  // Handle sortFunction event from PrimeNG table
  /**
   * Handles sort events from the UI, updates the component's current sort field and order, and triggers client-side sorting.
   * @example
   * nSortFunction({ field: 'date', order: 1 })
   * undefined
   * @param {{any}} {{event}} - Sort event object containing `field` (the field to sort by) and `order` (1 = asc, -1 = desc, 0 = none).
   * @returns {{void}} Does not return a value; updates internal sort state and calls performClientSideSort.
   **/
  onSortFunction(event: any): void {
    if (!event || typeof event !== 'object') {
      this.logger.warn('âš ï¸ Invalid sort event received:', event);
      return;
    }

    const field = event.field;
    const order = event.order; // 1 for asc, -1 for desc, 0 for none

    this.logger.debug('ðŸ”„ Sort function called:', { field, order });

    if (order === 0) {
      this.currentSortField = null;
      this.currentSortOrder = null;
    } else {
      this.currentSortField = field;
      this.currentSortOrder = order === 1 ? 'asc' : 'desc';
    }

    // Perform client-side sorting
    this.performClientSideSort();
  }

  // Client-side sorting implementation
  /**
   * Perform a client-side (in-memory) sort of this.appointments using the component's currentSortField and currentSortOrder.
   * @example
   * performClientSideSort()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Sorts the appointments array in place; returns nothing.
   **/
  private performClientSideSort(): void {
    if (!this.currentSortField || !this.currentSortOrder) {
      this.logger.debug('ðŸ”„ No active sort state, skipping client-side sort');
      return;
    }

    if (!Array.isArray(this.appointments) || this.appointments.length === 0) {
      this.logger.debug('ðŸ”„ No appointments to sort');
      return;
    }

    this.logger.debug('ðŸ”„ Performing client-side sort:', {
      field: this.currentSortField,
      order: this.currentSortOrder,
      appointmentCount: this.appointments.length,
    });

    try {
      this.appointments.sort((a: any, b: any) => {
        const value1 = this.getFieldValue(a, this.currentSortField!);
        const value2 = this.getFieldValue(b, this.currentSortField!);

        let result = 0;

        if (value1 == null && value2 != null) {
          result = -1;
        } else if (value1 != null && value2 == null) {
          result = 1;
        } else if (value1 == null && value2 == null) {
          result = 0;
        } else if (this.currentSortField === 'appointment_time') {
          // Handle date sorting
          const date1 = new Date(value1);
          const date2 = new Date(value2);
          result = date1.getTime() - date2.getTime();
        } else if (typeof value1 === 'string' && typeof value2 === 'string') {
          // Handle string sorting
          result = value1.localeCompare(value2);
        } else if (typeof value1 === 'number' && typeof value2 === 'number') {
          // Handle number sorting
          result = value1 - value2;
        } else {
          // Handle other types
          result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
        }

        return this.currentSortOrder === 'asc' ? result : -result;
      });

      this.logger.debug('âœ… Client-side sorting completed successfully');
    } catch (error) {
      this.logger.error('âŒ Error during client-side sorting:', error);
    }
  }

  // Helper method to get field value from object
  private getFieldValue(obj: any, field: string): any {
    return field.split('.').reduce((o, f) => o && o[f], obj);
  }

  getSortState(field: string): 'asc' | 'desc' | 'none' {
    if (this.currentSortField === field) {
      return this.currentSortOrder as 'asc' | 'desc';
    }
    return 'none';
  }

  /**
   * Returns the file path of the sort icon for a given table field based on the current sort state.
   * @example
   * etSortIcon('appointment_code')
   * 'assets/images/package/sort-ascending-numbers.svg'
   * @param {string} field - Field name used to determine which sort icon to return (special cases: 'appointment_code', 'appointment_time').
   * @returns {string} Path to the icon image corresponding to the field's sort state ('asc', 'desc', or default).
   **/
  getSortIcon(field: string): string {
    const sortState = this.getSortState(field);

    switch (sortState) {
      case 'asc':
        if (field === 'appointment_code') {
          return 'assets/images/package/sort-ascending-numbers.svg';
          // return 'assets/images/customer/tabler_sort-ascending-letters.svg';
        } else if (field === 'appointment_time') {
          return 'assets/images/package/sort-ascending-numbers.svg';
        } else {
          return 'assets/images/customer/tabler_sort-ascending-letters.svg';
        }
      case 'desc':
        if (field === 'appointment_code') {
          // return 'assets/images/customer/sort-descending-letters.svg';
          return 'assets/images/package/sort-descending-numbers.svg';
        } else if (field === 'appointment_time') {
          return 'assets/images/package/sort-descending-numbers.svg';
        } else {
          return 'assets/images/customer/sort-descending-letters.svg';
        }
      default:
        return 'assets/images/package/tabler_arrows-sort.svg';
    }
  }

  // Debug method - can be called from browser console
  testTableUpdate(): void {
    this.logger.debug('ðŸ§ª Testing manual table update...');
    this.loadAppointments();
  }

  // Service filter methods
  get hasServiceFilterValues(): boolean {
    return this.appliedServiceFilter.length > 0;
  }

  toggleServiceFilter(event: Event): void {
    event.stopPropagation();
    this.isServiceFilterOpen = !this.isServiceFilterOpen;

    if (this.isServiceFilterOpen) {
      this.loadServiceOptions();
      this.selectedServices = [...this.appliedServiceFilter];
      // Filter panel handled by Material popover in template
    }
  }

  // Status filter methods
  get hasStatusFilterValues(): boolean {
    return this.appliedStatusFilter.length > 0;
  }

  toggleStatusFilter(event: Event): void {
    event.stopPropagation();
    this.isStatusFilterOpen = !this.isStatusFilterOpen;

    if (this.isStatusFilterOpen) {
      this.selectedStatuses = [...this.appliedStatusFilter];
      // Filter panel handled by Material popover in template
    }
  }

  // Employee filter methods
  get hasEmployeeFilterValues(): boolean {
    return this.appliedEmployeeFilter.length > 0;
  }

  toggleEmployeeFilter(event: Event): void {
    event.stopPropagation();
    this.isEmployeeFilterOpen = !this.isEmployeeFilterOpen;

    if (this.isEmployeeFilterOpen) {
      this.loadEmployeeOptions();
      this.selectedEmployees = [...this.appliedEmployeeFilter];
      // Filter panel handled by Material popover in template
    }
  }

  // Location filter methods
  get hasLocationFilterValues(): boolean {
    return this.appliedLocationFilter.length > 0;
  }

  toggleLocationFilter(event: Event): void {
    event.stopPropagation();
    this.isLocationFilterOpen = !this.isLocationFilterOpen;

    if (this.isLocationFilterOpen) {
      this.loadLocationOptions();
      this.selectedLocations = [...this.appliedLocationFilter];
      // Filter panel handled by Material popover in template
    }
  }

  /**
   * Load car wash package options into this.serviceOptions based on user role (admin or staff), managing the packageSubscription and handling errors.
   * @example
   * loadServiceOptions()
   * // this.serviceOptions becomes: [{ label: 'Premium Detail', value: 'Premium Detail' }]
   * @returns {{void}} No return value; updates this.serviceOptions and manages subscriptions.
   **/
  private loadServiceOptions(): void {
    // Unsubscribe from previous subscription if exists
    if (this.packageSubscription) {
      this.packageSubscription.unsubscribe();
    }

    if (!this.isAdminUser) {
      // Staff user: use staff-specific API (same as sales module)
      this.packageSubscription = this.packageService
        .getCarWashPackagesForStaff()
        .pipe(
          catchError(error => {
            this.logger.error('Error loading staff packages:', error);
            return of([]);
          })
        )
        .subscribe(packages => {
          // Filter for detailing packages only and active packages
          const detailingPackages = packages.filter(
            pkg => pkg.package_status && pkg.wash_types && pkg.wash_types.includes('detailing')
          );

          this.serviceOptions = detailingPackages.map(pkg => ({
            label: pkg.package_name,
            value: pkg.package_name,
          }));
        });
    } else {
      // Admin user: use regular loadPackages API
      this.packageService.loadPackages();

      this.packageSubscription = this.packageService.packages$.subscribe(packages => {
        // Show all active packages for admin
        const activePackages = packages.filter(pkg => pkg.package_status);

        this.serviceOptions = activePackages.map(pkg => ({
          label: pkg.package_name,
          value: pkg.package_name,
        }));
      });
    }
  }

  private loadEmployeeOptions(): void {
    // Get unique employee names from original appointments
    const uniqueEmployees = [...new Set(this.originalAppointments.map(app => app.employee_name))];
    this.employeeOptions = uniqueEmployees.map(employee => ({
      label: employee,
      value: employee,
    }));
  }

  /**
   * Load location options from the profile service, update this.locationOptions with {label, value} pairs, and manage the subscription.
   * @example
   * loadLocationOptions()
   * void
   * @returns {void} Does not return a value.
   **/
  private loadLocationOptions(): void {
    // Load locations from profile service
    this.profileService.loadLocations();

    // Unsubscribe from previous subscription if exists
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }

    this.locationSubscription = this.profileService.locations$.subscribe(locations => {
      this.locationOptions = locations.map(location => ({
        label: location.branch,
        value: location.branch,
      }));
    });
  }

  /**
   * Unsubscribes from all active subscriptions on the component to prevent memory leaks.
   * @example
   * gOnDestroy()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} No return value; performs cleanup by unsubscribing from subscriptions.
   **/
  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.packageSubscription) {
      this.packageSubscription.unsubscribe();
    }
    if (this.employeeSubscription) {
      this.employeeSubscription.unsubscribe();
    }
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
    this.routeSubscription.unsubscribe();
  }

  /**
   * Apply all active filters (service, status, employee, location) to the component's originalAppointments and update the appointments; then perform client-side sort if a sort field and order are set.
   * @example
   * applyAllActiveFilters()
   * undefined
   * @returns {{void}} No return value; updates this.appointments and triggers client-side sort when applicable.
   **/
  private applyAllActiveFilters(): void {
    let filteredAppointments = [...this.originalAppointments];
    if (this.appliedServiceFilter.length > 0) {
      filteredAppointments = filteredAppointments.filter(app =>
        this.appliedServiceFilter.includes(app.package_name)
      );
    }
    if (this.appliedStatusFilter.length > 0) {
      filteredAppointments = filteredAppointments.filter(app =>
        this.appliedStatusFilter.includes(app.appointment_status)
      );
    }
    if (this.appliedEmployeeFilter.length > 0) {
      filteredAppointments = filteredAppointments.filter(app =>
        this.appliedEmployeeFilter.includes(app.employee_name)
      );
    }
    if (this.appliedLocationFilter.length > 0) {
      filteredAppointments = filteredAppointments.filter(app =>
        this.appliedLocationFilter.includes(app.branch)
      );
    }
    this.appointments = filteredAppointments;
    this.dataSource.data = filteredAppointments;
    if (this.currentSortField && this.currentSortOrder) {
      this.performClientSideSort();
    }
  }

  clearServiceFilter(): void {
    this.selectedServices = [];
    this.appliedServiceFilter = [];
    this.isServiceFilterOpen = false;

    this.applyAllActiveFilters();
  }

  applyServiceFilter(): void {
    this.appliedServiceFilter = [...this.selectedServices];
    this.isServiceFilterOpen = false;

    this.applyAllActiveFilters();
  }

  clearStatusFilter(): void {
    this.selectedStatuses = [];
    this.appliedStatusFilter = [];
    this.isStatusFilterOpen = false;

    this.applyAllActiveFilters();
  }

  applyStatusFilter(): void {
    this.appliedStatusFilter = [...this.selectedStatuses];
    this.isStatusFilterOpen = false;

    this.applyAllActiveFilters();
  }

  clearEmployeeFilter(): void {
    this.selectedEmployees = [];
    this.appliedEmployeeFilter = [];
    this.isEmployeeFilterOpen = false;

    this.applyAllActiveFilters();
  }

  applyEmployeeFilter(): void {
    this.appliedEmployeeFilter = [...this.selectedEmployees];
    this.isEmployeeFilterOpen = false;

    this.applyAllActiveFilters();
  }

  clearLocationFilter(): void {
    this.selectedLocations = [];
    this.appliedLocationFilter = [];
    this.isLocationFilterOpen = false;

    this.applyAllActiveFilters();
  }

  applyLocationFilter(): void {
    this.appliedLocationFilter = [...this.selectedLocations];
    this.isLocationFilterOpen = false;

    this.applyAllActiveFilters();
  }

  // Method to handle "Add New Appointment" button click
  onAddNewAppointment(): void {
    this.logger.debug('Add new appointment clicked');
    this.logger.debug('Current emptyStateConfig:', this.emptyStateConfig);
    this.logger.debug('Has active search or filters:', this.hasActiveSearchOrFilters);
    this.logger.debug('Appointments count:', this.appointments.length);
    this.addNewAppointment.emit();
  }

  formatDuration(duration: { minutes?: number; hours?: number } | null | undefined): string {
    return formatDuration(duration);
  }

  trackByOption(_: number, opt: { value: string }): string {
    return opt.value;
  }
}
